/**
 * PATH: packages/ui/src/components/settings/team-tab/MemberRow.tsx
 * PURPOSE: Single team-member row with avatar, info, and action menu
 *
 * WHY: Extracted from TeamTab.tsx to keep the orchestrator under 300 lines.
 *      The row is a pure presentational component — all callbacks are injected.
 *
 * FLOW:
 *   ┌────────────┐    ┌──────────────┐    ┌───────────────────┐
 *   │ TeamMember │───▶│ MemberRow    │───▶│ Avatar/Info/Menu  │
 *   └────────────┘    └──────────────┘    └───────────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../../../lib/utils: cn() class merging
 *   - ./team-tab-primitives: TeamMember, RoleOption, getRoleIcon, formatDate
 */

import {
  KeyRound,
  Mail,
  MoreVertical,
  Trash2,
  RefreshCw,
  Clock,
  ShieldCheck,
  Check,
  Square,
} from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { TeamMember, RoleOption } from './team-tab-primitives'
import { getRoleIcon, formatDate } from './team-tab-primitives'

/**
 * RBAC V2 MR 19 FE (2026-05-29) — capability toggle field names. These
 * mirror the BE OrganisationMember boolean columns. Centralised here so
 * a typo in one of the 3 menu items can't drift away from the others.
 */
type ToggleField = 'has_finance_org_wide' | 'is_org_admin_user' | 'is_billing_admin'

export interface MemberRowProps {
  member: TeamMember
  isAdmin: boolean
  isSelf: boolean
  roleOptions: RoleOption[]
  activeMenu: number | null
  actionLoading: number | null
  onRoleChange: (member: TeamMember, newRole: string) => void
  onRemove: (member: TeamMember) => void
  onResendInvite: (memberId: number, memberEmail: string) => void
  onToggleMenu: (memberId: number) => void
  /**
   * RBAC V2 MR 19 FE — inline-edit handler for the 3 capability toggles
   * (CFO / Team admin / Billing). Optional so older consumers of this
   * component that don't yet wire up the handler keep working — the
   * toggle menu items simply don't render when the prop is absent.
   */
  onToggleUpdate?: (member: TeamMember, field: ToggleField, newValue: boolean) => void
}

export function MemberRow({
  member,
  isAdmin,
  isSelf,
  activeMenu,
  actionLoading,
  onRemove,
  onResendInvite,
  onToggleMenu,
  onToggleUpdate,
}: MemberRowProps) {
  const displayRole = member.rbac_role_name || member.role
  const canShowActions = isAdmin && !isSelf
  // RBAC V2 MR 19 FE — owners always have all 3 toggles ON (BE
  // force-True rule, see organisation/views/team_members_views/
  // list_create_views.py + detail_views.py). Hiding the toggle items
  // for owner-tier rows avoids a confusing UI where clicking the
  // toggle appears to do nothing (BE silently re-asserts True on save).
  const isOwnerRole = (member.rbac_role_code || member.role || '').toLowerCase().includes('owner')
  const canEditToggles = canShowActions && !isOwnerRole && !!onToggleUpdate
  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg border",
        "border-slate-200 dark:border-slate-700",
        "bg-white dark:bg-slate-800/50",
        "hover:bg-slate-50 dark:hover:bg-slate-800"
      )}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
        {member.profile_image ? (
          <img
            src={member.profile_image}
            alt={member.full_name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
            {member.first_name?.[0] || member.email[0].toUpperCase()}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 dark:text-white truncate">
            {member.full_name || member.email}
          </span>
          {getRoleIcon(member.role)}
          {member.is_temp_password && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
              Pending
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
          <span className="truncate">{member.email}</span>
          <span className="text-slate-300 dark:text-slate-600">&bull;</span>
          <span className="capitalize">{displayRole}</span>
          {isSelf && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
              You
            </span>
          )}
          {/*
            RBAC V2 (MR 11 / MR 12) - capability toggle badges.
            Render one chip per active toggle so admins can scan
            the team table and see at a glance who has CFO /
            team-admin / billing access. Owner-tier shows none of
            these (they always have everything - the role label
            already conveys it; a row crowded with 3 redundant
            chips would just be noise). For non-owner rows, missing
            toggle fields (older API responses) render as no chip.
          */}
          {member.role !== 'owner' && member.has_finance_org_wide && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
              title="Has access to the CFO module (org-wide finance)"
            >
              CFO
            </span>
          )}
          {member.role !== 'owner' && member.is_org_admin_user && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400"
              title="Can invite + manage team members"
            >
              Team admin
            </span>
          )}
          {member.role !== 'owner' && member.is_billing_admin && (
            <span
              className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
              title="Can view + change Quotech subscription"
            >
              Billing
            </span>
          )}
        </div>
      </div>

      {/* Last login */}
      <div className="hidden sm:flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
        <Clock className="w-3 h-3" />
        <span>{formatDate(member.last_login)}</span>
      </div>

      {/* Actions — hidden for the current user (self) */}
      {canShowActions && (
        <div className="relative">
          <button
            onClick={() => onToggleMenu(member.id)}
            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            {actionLoading === member.id ? (
              <RefreshCw className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <MoreVertical className="w-4 h-4 text-slate-400" />
            )}
          </button>

          {activeMenu === member.id && (
            <div className={cn(
              "absolute right-0 top-full mt-1 w-60 z-50",
              "rounded-lg border border-slate-200 dark:border-slate-700",
              "bg-white dark:bg-slate-800 shadow-lg"
            )}>
              <div className="p-1">
                <div className="px-2 py-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 border-b border-slate-100 dark:border-slate-700 mb-1">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  <span>To change roles, go to <strong className="text-slate-700 dark:text-slate-300">Access Control</strong></span>
                </div>

                {/*
                  RBAC V2 MR 19 FE (2026-05-29) - inline capability toggles.
                  Three click-to-flip items, one per OrganisationMember
                  boolean. Owner rows skip this block (canEditToggles is
                  false) because the BE force-True's all 3 for owners and
                  a click would appear to do nothing. Self also skips
                  (canShowActions is already false for isSelf).

                  Operator-reported (2026-05-29): a Bravada org owner
                  asked "if an owner wants to give CFO access to someone,
                  how can that be done?" - answer was "today only at
                  invite time or via SSH". This block closes that gap.
                */}
                {canEditToggles && (
                  <>
                    <div className="px-2 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                      Capabilities
                    </div>
                    <ToggleMenuItem
                      label="CFO module access"
                      sublabel="Org-wide finance reports + bills"
                      checked={!!member.has_finance_org_wide}
                      onClick={() => onToggleUpdate?.(member, 'has_finance_org_wide', !member.has_finance_org_wide)}
                    />
                    <ToggleMenuItem
                      label="Team admin"
                      sublabel="Invite + manage team members"
                      checked={!!member.is_org_admin_user}
                      onClick={() => onToggleUpdate?.(member, 'is_org_admin_user', !member.is_org_admin_user)}
                    />
                    <ToggleMenuItem
                      label="Billing admin"
                      sublabel="View + change Quotech subscription"
                      checked={!!member.is_billing_admin}
                      onClick={() => onToggleUpdate?.(member, 'is_billing_admin', !member.is_billing_admin)}
                    />
                    <div className="my-1 border-t border-slate-100 dark:border-slate-700" />
                  </>
                )}

                {/* Same backend endpoint either way -- the parent
                    decides whether to confirm based on the member's
                    activation state. The label flips so the recipient
                    knows whether to expect "complete your invite" vs
                    "your password has been reset". */}
                <button
                  onClick={() => onResendInvite(member.id, member.email)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                >
                  {member.is_temp_password ? (
                    <>
                      <Mail className="w-4 h-4" />
                      Resend invite
                    </>
                  ) : (
                    <>
                      <KeyRound className="w-4 h-4" />
                      Reset password
                    </>
                  )}
                </button>
                <button
                  onClick={() => onRemove(member)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * RBAC V2 MR 19 FE — small click-to-flip menu item rendered in the
 * MemberRow action menu, one per capability toggle. Kept as a local
 * sub-component (not exported) because its visual contract is tightly
 * coupled to the action-menu's spacing/typography and we never want it
 * reused elsewhere as a generic checkbox.
 */
function ToggleMenuItem({
  label,
  sublabel,
  checked,
  onClick,
}: {
  label: string
  sublabel: string
  checked: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-2 px-2 py-1.5 rounded text-sm',
        'hover:bg-slate-100 dark:hover:bg-slate-700',
        'text-left',
      )}
      title={`${checked ? 'Remove' : 'Grant'} ${label.toLowerCase()}`}
    >
      <span
        className={cn(
          'mt-0.5 flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center',
          checked
            ? 'bg-teal-600 border-teal-600 text-white'
            : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800',
        )}
        aria-hidden="true"
      >
        {checked ? <Check className="w-3 h-3" /> : <Square className="w-3 h-3 opacity-0" />}
      </span>
      <span className="flex flex-col leading-tight">
        <span className={cn('font-medium', checked ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300')}>
          {label}
        </span>
        <span className="text-[11px] text-slate-500 dark:text-slate-400">{sublabel}</span>
      </span>
    </button>
  )
}
